import { DEFAULT_OVERDUE_GRACE_MS, isScheduledJobOverdue } from "@nexus/scheduler/job-health";
import type { ScheduledJob } from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import { humanizeSlug } from "@/lib/slug-labels";
import { checkCronSchedule, type CronSummaryKey } from "./scheduled-job-cron";

export const OVERDUE_GRACE_MS = DEFAULT_OVERDUE_GRACE_MS;

export { isScheduledJobOverdue };

export function getScheduledJobCadenceDisplay(
  job: Pick<ScheduledJob, "schedule" | "timeZone">,
): string {
  if (!job.schedule) return "—";
  return job.timeZone ? `${job.schedule} (${job.timeZone})` : job.schedule;
}

/**
 * What a job's cron says, in the terms the create form reads it back in.
 *
 * Five fields tell an operator when a job runs only after they have parsed them,
 * and a list of eight jobs is eight parses. The key and its params come from the
 * same `checkCronSchedule` the form's readback uses, so a schedule cannot be
 * described one way while it is being set and another way once it is listed.
 *
 * Null for a schedule that is absent, invalid, or too intricate to phrase —
 * `generic` is the check's own admission that it has nothing better than the
 * expression to offer, and the expression is already on the row.
 */
export type ScheduledJobCadenceSummary = {
  key: Exclude<CronSummaryKey, "generic">;
  params: Record<string, string>;
};

export function getScheduledJobCadenceSummary(
  job: Pick<ScheduledJob, "schedule">,
): ScheduledJobCadenceSummary | null {
  if (!job.schedule) return null;

  const check = checkCronSchedule(job.schedule);
  if (!check.ok || check.summaryKey === "generic") return null;

  return { key: check.summaryKey, params: check.summaryParams ?? {} };
}

/** `admin-ops-sweep` → `Admin ops sweep` for list cells and tooltips. */
export function humanizeScheduledJobId(jobId: string): string {
  return humanizeSlug(jobId);
}

/**
 * Tooltip copy: human label, with the machine id when it differs so operators
 * can still grep Cloud Scheduler / logs from the hover.
 */
export function scheduledJobIdTooltip(jobId: string): string {
  const label = humanizeScheduledJobId(jobId);
  return label === jobId ? jobId : `${label} (${jobId})`;
}
